// Annotation System API for Synapsys.Octoplex
// This file handles all communication with the collaborative PDF annotation system

import axios, { HttpStatusCode } from "axios";

const AnnotationAPI = {
 
  uploadPdfToAnnotation: async (file, attachmentId, userId, token, companyId, meetingId = null, meetingDate = null, originalDate = null, cb = null) => {
    try {
      const formData = new FormData();
      formData.append("pdf", file);
      formData.append("attachmentId", attachmentId);
      formData.append("userId", userId);
      formData.append("fileName", file.name);
      if (meetingId) formData.append("meetingId", meetingId);
      if (meetingDate) formData.append("meetingDate", meetingDate);
      if (originalDate) formData.append("originalDate", originalDate);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'CompanyId': companyId
        },
        onUploadProgress: function(progressEvent) {
              const percentCompleted = Math.round( (progressEvent.loaded * 100) / progressEvent.total );
              // update progress callback
              if(cb) cb(percentCompleted);              
            }
      }

      const response = await axios.post(`${process.env.REACT_APP_ANNOTATION_API_URL}/api/integration/upload-pdf-direct`, formData, config);      

      if (response.status !== HttpStatusCode.Created) {
        throw new Error(`Annotation system upload failed: ${response.status} ${response.statusText}`);
      }

      return response.data;
    } catch (error) {
      console.error('Error uploading to annotation system:', error);
      throw error;
    }
  },

  /**
   * Get session information by attachment ID
   * @param {string} attachmentId - Attachment ID to look up
   * @param {string} token - JWT token from main system
   * @param {string} companyId - Company ID for context
   * @returns {Promise<Object>} Session information
   */
  getSessionByAttachmentId: async (attachmentId, token, companyId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_ANNOTATION_API_URL}/api/integration/session/attachment/${attachmentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'CompanyId': companyId
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to get session: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting session from annotation system:', error);
      throw error;
    }
  },

  /**
   * Check annotation system health
   * @returns {Promise<Object>} Health status
   */
  checkHealth: async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_ANNOTATION_API_URL}/api/integration/health`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking annotation system health:', error);
      throw error;
    }
  },

  /**
   * Generate a unique attachment ID
   * @returns {string} Unique attachment ID
   */
  generateAttachmentId: () => {
    // FIX: Use more unique ID generation to prevent collisions
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const uuid = crypto.randomUUID ? crypto.randomUUID() : `${timestamp}_${random}_${Math.random().toString(36).substr(2, 5)}`;
    return `attachment_${timestamp}_${random}_${uuid.split('-')[0]}`;
  },

  /**
   * Check if annotation system is enabled
   * @returns {boolean} True if annotation system is enabled
   */
  isEnabled: () => {
    return process.env.REACT_APP_ANNOTATION_ENABLED === 'true';
  },

  /**
   * Get annotation system API URL (for uploading files)
   * @returns {string} Annotation system API URL
   */
  getApiUrl: () => {
    return process.env.REACT_APP_ANNOTATION_API_URL || 'http://localhost:3002';
  },

  /**
   * Get annotation system frontend URL (for viewing sessions)
   * @returns {string} Annotation system frontend URL
   */
  getFrontendUrl: () => {
    return process.env.REACT_APP_ANNOTATION_FRONTEND_URL || 'http://localhost:8080';
  },

  /**
   * Get annotation system base URL (deprecated - use getFrontendUrl instead)
   * @returns {string} Annotation system base URL
   */
  getBaseUrl: () => {
    return process.env.REACT_APP_ANNOTATION_FRONTEND_URL || 'http://localhost:8080';
  }
};

export default AnnotationAPI; 